/**
 * 저장된 번호를 국내 표기로 변환해 화면에 보여준다 (2026-08-13 KST 추가)
 * Saleor는 주소 연락처를 E.164(+821094560470)로 저장하는데 그대로 노출하면 읽기 어렵다.
 * 국내 번호로 해석되지 않으면 원본을 그대로 돌려준다.
 */

/**
 * 회원 연락처를 담는 공개 metadata 키 (2026-08-13 KST 추가)
 * Saleor User에는 전화번호 필드가 없어 스토어프론트 가입 라우트와 social_auth
 * 플러그인이 이 키에 저장한다.
 */
export const CUSTOMER_PHONE_METADATA_KEY = "farmstore.phone";

function normalizeKrPhoneDigits(value: string): string {
  let digits = value.replace(/\D/g, "");

  if (digits.startsWith("82") && digits.length >= 10) {
    const national = digits.slice(2);

    // 15xx·16xx·18xx 대표번호는 국내 표기에도 앞자리 0 이 없다 (2026-08-27 KST)
    digits = /^1[5678]/.test(national) ? national : `0${national}`;
  }

  return digits;
}

/**
 * 국내 번호 형식 검증 (2026-08-13 KST 추가)
 * 스토어프론트 가입·계정 설정과 같은 규칙이라, 대시보드에서 고친 번호도
 * 스토어프론트가 저장하는 값과 같은 형식으로 유지된다.
 */
export function isValidKrPhoneNumber(value: string): boolean {
  const digits = normalizeKrPhoneDigits(value);

  // 휴대폰(010은 11자리, 구 번호 011/016~019는 10~11자리)
  if (/^010\d{8}$/.test(digits) || /^01[16789]\d{7,8}$/.test(digits)) {
    return !/^0+$/.test(digits.slice(3));
  }

  // 서울 유선
  if (/^02\d{7,8}$/.test(digits)) {
    return !/^0+$/.test(digits.slice(2));
  }

  // 기타 지역번호·인터넷전화
  if (/^0(3[1-3]|4[1-4]|5[1-5]|6[1-4]|70)\d{7,8}$/.test(digits)) {
    return !/^0+$/.test(digits.slice(3));
  }

  return false;
}

export function formatKrPhoneDisplay(value: string): string {
  const digits = normalizeKrPhoneDigits(value);

  // 대표번호(15xx-xxxx, 16xx-xxxx, 18xx-xxxx) (2026-08-27 KST 추가)
  if (/^1[5678]\d{6}$/.test(digits)) {
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  }

  // 휴대폰: 010은 11자리, 구 번호(011/016~019)는 10~11자리
  if (/^01[016789]\d{7,8}$/.test(digits)) {
    return digits.length === 11
      ? `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
      : `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // 서울 유선(02)은 지역번호가 2자리
  if (/^02\d{7,8}$/.test(digits)) {
    const rest = digits.slice(2);

    return `02-${rest.slice(0, rest.length - 4)}-${rest.slice(-4)}`;
  }

  // 그 외 지역번호·인터넷전화(031~064, 070)
  if (/^0(3[1-3]|4[1-4]|5[1-5]|6[1-4]|70)\d{7,8}$/.test(digits)) {
    const rest = digits.slice(3);

    return `${digits.slice(0, 3)}-${rest.slice(0, rest.length - 4)}-${rest.slice(-4)}`;
  }

  return value;
}
